import React, { useState, useEffect, useRef, useCallback, memo, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  IconButton,
  DialogActions,
  Button,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useApp } from '../../context/AppContext';

const SubjectTermsDialog = memo(({ open, onClose }) => {
  const { terms, selectedSubject, setSelectedTerm, setSelectedSubject } = useApp();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  // 현재 선택된 과목의 용어만 필터링
  const filteredTerms = useMemo(() => {
    if (!selectedSubject || !terms) return [];

    if (selectedSubject === 'Must Know') {
      // Must Know는 is_must_know가 true인 term만, 알파벳 순으로 정렬
      return terms
        .filter(term => 
          term.is_must_know === true || term.is_must_know === 'true' || term.is_must_know === 1
        )
        .sort((a, b) => a.term.localeCompare(b.term));
    }

    // 기존 과목은 subject로 필터링 (이미 정렬되어 있음)
    return terms.filter(term => term.subject === selectedSubject);
  }, [selectedSubject, terms]);

  useEffect(() => {
    console.log('Dialog mounted/updated with:', { 
      category: selectedSubject, 
      open, 
      terms: filteredTerms.length,
    });
  }, [selectedSubject, open, filteredTerms.length]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedSubject || !open) return;

    try {
      setLoading(true);
      setError(null);

      console.log('용어 데이터 가져오는 중:', selectedSubject);

      if (filteredTerms.length === 0) {
        setError('이 과목에 대한 용어가 없습니다.');
      }

    } catch (err) {
      console.error('Error processing terms:', err);
      if (isMounted.current) {
        setError(err.message);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [selectedSubject, open, filteredTerms]);

  const handleTermClick = (term) => {
    if (!term) {
      console.error('선택된 용어가 없습니다');
      return;
    }
    
    // id가 없으면 _id 사용
    const termId = term.id || term._id;
    
    console.log('용어 클릭됨:', {
      term: term.term,
      subject: term.subject,
      id: termId,
      timestamp: new Date().toISOString()
    });
    
    // 용어와 과목 모두 선택
    setSelectedTerm(term);
    setSelectedSubject(term.subject);
    
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 16,
        sx: {
          minHeight: '50vh',
          maxHeight: '70vh',
          borderRadius: '12px',
          background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid #e0e0e0',
        background: 'white',
        py: 2
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: '#1a237e',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {selectedSubject || '과목'} 용어 목록
            {loading && <CircularProgress size={20} />}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : filteredTerms.length > 0 ? (
          <List>
            {filteredTerms.map((term, index) => (
              <ListItem 
                key={term._id || index}
                button 
                onClick={() => handleTermClick(term)}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <ListItemText
                  primary={term.term}
                  secondary={`정의: ${term.definition.substring(0, 100)}${term.definition.length > 100 ? '...' : ''}`}
                />
              </ListItem>
            ))}
          </List>
        ) : !loading ? (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              이 과목에 대한 용어가 없습니다.
            </Typography>
          </Box>
        ) : (
          <Box textAlign="center" py={4}>
            <CircularProgress size={40} />
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              용어를 불러오는 중입니다...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
});

export default SubjectTermsDialog; 